import React from "react";
import { Link, graphql, useStaticQuery } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";
import ProductImage from "./product/product-img";
import ProductTitle from "./product/product-title";
import ProductPrice from "./product/product-price";
import ProductButton from "./product/product-button";
import ProductHeart from "./product/product-heart";
import { parsePrice, truncateText, updateDataLayer } from "../utils/helpers";

const ProductWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--color-white);
  padding: 0.875rem;
  justify-content: ${({ $alignButtons }) =>
    $alignButtons ? "space-between" : "flex-start"};
  cursor: pointer;
  position: relative;
  border: 1px solid var(--color-grey);
  border-radius: 5px;
  transition: 0.3s all ease-in-out;
  > * {
    margin-block: 0.5rem;
  }
  max-width: ${({ $scrollHorizontalOnMobile }) =>
    $scrollHorizontalOnMobile ? "278px" : "none"};
  @media (hover: hover) {
    &:hover {
      box-shadow: var(--box-shadow);
      border: 1px solid var(--color-accent2);
      & h2 {
        text-decoration: underline;
      }
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    max-width: ${({ $scrollHorizontalOnMobile }) =>
      $scrollHorizontalOnMobile ? "200px" : "none"};
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    min-width: 170px;
    max-width: none;
    max-height: 500px;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-block: 0.25rem !important;
  > * {
    padding-block: 0.25rem;
  }
`;

const Title = styled(ProductTitle)`
  flex-grow: 1;
  font-size: 0.875rem;
  font-weight: 800;
  margin: 0;
`;
const ProductExcerpt = styled.div`
  p {
    font-size: 0.775rem;
    margin: 0;
  }
`;
const RowWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  height: 100%;
  justify-content: space-between;
  & a {
    display: flex;
    width: 80%;
    p {
      flex-grow: 1;
    }
  }
`;

const Brand = styled.p`
  margin: 0;
  opacity: 0.6;
  font-size: 0.75rem;
  min-height: 28px;
`;

const ProductBlock = ({
  product,
  showAddToCartButton,
  showPrice,
  showPriceRange = false,
  showPriceModified,
  showTitle,
  alignButtons,
  scrollHorizontalOnMobile,
  heartIconSize,
  showExcerpt,
}) => {
  const { wp } = useStaticQuery(graphql`
    query {
      wp {
        wcSettings {
          currency
          priceDecimalSeparator
          priceThousandSeparator
        }
      }
    }
  `);

  const priceModified = product?.price?.replaceAll("kr", "");
  const formatPrice = (price) => {
    const numericPriceString = price.replace(/[^\d.-]/g, "");

    if (!showPriceRange && numericPriceString.split("-").length > 1) {
      return parseFloat(numericPriceString.split("-")[0]).toString() + " :-";
    }
    return parseFloat(numericPriceString).toString() + ",-";
  };

  const onProductLinkClick = () => {
    if (typeof window === "undefined") {
      return;
    }

    const priceRaw =
      product.price &&
      parsePrice(
        product.price,
        wp.wcSettings.priceThousandSeparator,
        wp.wcSettings.priceDecimalSeparator
      );
    updateDataLayer(async () =>
      window.dataLayer.push({
        event: "select_item",
        ecommerce: {
          currency: wp.wcSettings.currency,
          value: priceRaw,
          items: [
            {
              item_id: product?.databaseId,
              item_name: product?.name,
              item_category: product?.productCategories?.nodes?.[0]?.name,
              item_brand: product?.productBrands?.nodes?.[0]?.name,
              price: priceRaw,
              quantity: 1,
            },
          ],
        },
      })
    );
  };

  return (
    <ProductWrapper
      $alignButtons={alignButtons}
      $scrollHorizontalOnMobile={scrollHorizontalOnMobile}
    >
      <StyledLink
        to={product?.uri || product.link}
        onClick={onProductLinkClick}
      >
        <ProductImage image={product?.image} />
        <Brand>{product?.productBrands?.nodes[0]?.name}</Brand>
      </StyledLink>
      <StyledLink
        to={product?.uri || product.link}
        onClick={onProductLinkClick}
      >
        {showTitle && (
          <Title
            name={product?.name}
            align="left"
            fontSize="medium"
            textColor="black"
          />
        )}
      </StyledLink>
      {showExcerpt && (
        <ProductExcerpt
          dangerouslySetInnerHTML={{
            __html: truncateText(product?.shortDescription, 50),
          }}
        ></ProductExcerpt>
      )}
      <RowWrapper>
        <StyledLink to={product?.uri} onClick={onProductLinkClick}>
          {showPrice && (
            <ProductPrice
              product={product}
              align="left"
              customFontSize={14}
              saleColor="accent1"
            />
          )}
          {showPriceModified && (
            <div
              style={{ fontSize: 14 }}
              dangerouslySetInnerHTML={{
                __html: priceModified && formatPrice(priceModified),
              }}
            />
          )}
        </StyledLink>
        <ProductHeart
          product={product}
          priceRaw={
            product.price &&
            parsePrice(
              product.price,
              wp.wcSettings.priceThousandSeparator,
              wp.wcSettings.priceDecimalSeparator
            )
          }
          size={heartIconSize}
          currency={wp.wcSettings.currency}
        />
      </RowWrapper>
      {showAddToCartButton && (
        <ProductButton
          product={product}
          alignButtons={alignButtons}
          currency={wp.wcSettings.currency}
          priceDecimalSeparator={wp.wcSettings.priceDecimalSeparator}
          priceThousandSeparator={wp.wcSettings.priceThousandSeparator}
          isVariation={product?.variations?.nodes?.length > 0}
        />
      )}
    </ProductWrapper>
  );
};

ProductBlock.propTypes = {
  product: PropTypes.shape({
    image: PropTypes.object,
    name: PropTypes.string,
    price: PropTypes.string,
    databaseId: PropTypes.number,
    priceRaw: PropTypes.number,
    shortDescription: PropTypes.string,
    productCategories: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.object),
    }),
    productBrands: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.object),
    }),
    uri: PropTypes.string,
    link: PropTypes.string,
    variations: PropTypes.shape({
      nodes: PropTypes.arrayOf(PropTypes.object),
    }),
  }),
  showAddToCartButton: PropTypes.bool,
  showPrice: PropTypes.bool,
  showPriceRange: PropTypes.bool,
  showPriceModified: PropTypes.bool,
  showTitle: PropTypes.bool,
  showExcerpt: PropTypes.bool,
  alignButtons: PropTypes.bool,
  currency: PropTypes.string,
  scrollHorizontalOnMobile: PropTypes.bool,
  heartIconSize: PropTypes.oneOf(["small", "medium", "large"]),
};

export default ProductBlock;
